/**
 * ============================================================================
 * 🔧 前台系统配置（运行时从后端获取）
 * ============================================================================
 * 
 * 所有品牌/系统名称等配置由后端 /api/system/config 接口动态提供。
 * 部署给新客户时，只需修改后端的 .env 文件即可，前端自动适配。
 * 
 * 使用方式：
 *   import { useSystemConfig, SystemConfigProvider } from '../config/system';
 *   ...
 *   const config = useSystemConfig();
 * 
 * 全局默认值（后端获取失败时使用）
 * ============================================================================
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

// ============================================================================
// 默认配置（后端不可用时的 fallback）
// ============================================================================
const DEFAULT_CONFIG = {
  companyName: '维修生产管理系统',
  companyShortName: '维修',
  systemTitle: '生产管理系统',
  industryDesc: '信息技术维修部生产系统',
  bigscreenTitle: '维修生产大屏',
  domain: '',
  defaults: {
    expressCompany: '京东快递',
    partsUnit: '个',
    partsMinStock: 5,
    warrantyMonths: 12,
  },
  features: {
    showLoginHint: true,
    showDefaultPassword: true,
  },
  paymentEnabled: {
    wechat: false,
    alipay: false,
  },
};

// ============================================================================
// React Context
// ============================================================================
const SystemConfigContext = createContext(DEFAULT_CONFIG);

let cachedConfig = null;

/**
 * 从后端获取公开系统配置
 */
async function fetchSystemConfig() {
  if (cachedConfig) return cachedConfig;
  try {
    // 3秒超时保护，避免阻塞整个页面渲染
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const res = await api.get('/system/config', { signal: controller.signal });
    clearTimeout(timeoutId);
    // 拦截器已解包，res = { code: 200, data: {...} }
    if (res && res.code === 200 && res.data) {
      cachedConfig = { ...DEFAULT_CONFIG, ...res.data };
      return cachedConfig;
    }
  } catch (e) {
    console.warn('[系统配置] 无法从后端获取配置，使用默认值');
  }
  cachedConfig = DEFAULT_CONFIG;
  return cachedConfig;
}

/**
 * 全局配置提供者组件
 * 在 App 根组件中包裹，自动加载后端配置
 */
export function SystemConfigProvider({ children }) {
  const [config, setConfig] = useState(cachedConfig || DEFAULT_CONFIG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchSystemConfig().then(cfg => {
      setConfig(cfg);
      setLoaded(true);
      // 动态设置页面标题
      document.title = `${cfg.companyShortName}${cfg.systemTitle}`;
    });
  }, []);

  if (!loaded && !cachedConfig) {
    // 配置加载中，显示空（通常瞬间完成）
    return null;
  }

  return (
    <SystemConfigContext.Provider value={config}>
      {children}
    </SystemConfigContext.Provider>
  );
}

/**
 * 获取系统配置 Hook
 */
export function useSystemConfig() {
  return useContext(SystemConfigContext);
}

/**
 * 非 React 环境下使用（比如 axios 拦截器）
 */
export function getSystemConfigSync() {
  return cachedConfig || DEFAULT_CONFIG;
}

export default {
  DEFAULT_CONFIG,
  fetchSystemConfig,
  getSystemConfigSync,
};
